import { DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { leaveLink } from '../../../../functions/claim/leaveLink';
import { LeaveCalendar } from '../../../../models/api/view/claim/summary/leavecalendar';
import { ExportService } from '../../../../services/export/export.service';
import { LeaveExportMapper } from '../../../../utilities/leave-export-mapper';
import { BaseComponent } from '../../../base/base.component';
import {
  ExportButtonComponent,
  ExportFormat,
} from '../../../core/buttons/export-button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { getDateKey } from './leave-calendar-utils';
import { filterAbsencesForCalendar } from './leave-calendar-filter';
import { LeaveCalendarWeeklyComponent } from './leave-calendar-weekly.component';
import { LeaveCalendarComponent } from './leave-calendar.component';
import { Day } from './month-view.component';

@Component({
  selector: 'app-leave-calendar-page',
  imports: [
    LeaveCalendarWeeklyComponent,
    LeaveCalendarComponent,
    TabComponent,
    TabPanelComponent,
    PageHeaderComponent,
    ExportButtonComponent,
  ],
  template: ` <app-page-header label="Leave calendar"></app-page-header>
    <app-tab-panel (valueChange)="onTabChange($event)">
      <app-export-button
        tabPanelRight
        [csv]="true"
        [xlsx]="true"
        [pdf]="false"
        right2
        (export)="onExport($event)"
      ></app-export-button>
      <app-tab label="Yearly view">
        <ng-template #content>
          @if (activeTabIndex === 0) {
            <app-leave-calendar
              calendar="yearly"
              [absencesByDate]="absencesByDate"
              [dateMenu]="dateMenu"
              [level]="level"
              [claim]="claim"
              [levels]="levels"
              [claims]="claims"
              [loading]="loading"
            />
          }
        </ng-template>
      </app-tab>
      <app-tab label="Monthly view">
        <ng-template #content>
          @if (activeTabIndex === 1) {
            <app-leave-calendar
              [absencesByDate]="absencesByDate"
              [dateMenu]="dateMenu"
              [level]="level"
              [claim]="claim"
              [levels]="levels"
              [claims]="claims"
              [loading]="loading"
            />
          }
        </ng-template>
      </app-tab>
      <app-tab label="Weekly view">
        <ng-template #content>
          @if (activeTabIndex === 2) {
            <app-leave-calendar-weekly
              [absences]="approvedAbsences"
              [loading]="loading"
            />
          }
        </ng-template>
      </app-tab>
    </app-tab-panel>`,
  styles: ``,
})
export class LeaveCalendarPageComponent
  extends BaseComponent
  implements OnInit
{
  exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  absences: LeaveCalendar[] = [];
  filteredAbsences: LeaveCalendar[] = [];
  approvedAbsences: LeaveCalendar[] = [];
  loading = false;

  // Form controls for filtering
  level = new FormControl<string>('claim');
  claim = new FormControl<string>('');

  // Options for dropdowns
  levels = [
    { description: 'Employee', value: 'employee' },
    { description: 'Claim', value: 'claim' },
  ];
  claims = [{ description: 'Claim Number', value: '' }];

  // Cached date menu for calendar navigation
  dateMenu: Day[] = [];

  // Indexed absences map for O(1) lookup performance
  absencesByDate = new Map<string, LeaveCalendar[]>();

  // Active tab tracking for lazy loading
  activeTabIndex = 0;

  // Column definitions for export
  // Note: Header text is defined in LeaveExportMapper.getHeaderText()
  private readonly exportColumns = [
    { propertyName: 'date', format: 'date' },
    { propertyName: 'employeeName' },
    { propertyName: 'type' },
    { propertyName: 'status' },
    { propertyName: 'subStatus' },
    { propertyName: 'absenceNumber' },
    { propertyName: 'hoursAbsent' },
    { propertyName: 'absenceConfirmationNumber' },
    { propertyName: 'absenceReason' },
  ];

  constructor() {
    super();

    // Subscribe to level changes - clear claim when switching to employee
    this.level.valueChanges
      .pipe(debounceTime(150), takeUntilDestroyed())
      .subscribe(() => {
        if (this.level.value === 'employee') {
          this.claim.setValue('', { emitEvent: false });
        }
        // Combined operation for better performance
        this.updateFiltersAndView();
      });

    // Claim selector functionality - commented out as claim dropdown is not currently visible
    // This could be re-enabled down the line if needed for filtering by specific claims
    // Subscribe to claim changes - debounced for rapid changes
    /* this.claim.valueChanges.pipe(
      debounceTime(150),
      takeUntilDestroyed()
    ).subscribe(() => {
      this.updateFiltersAndView();
    }); */
  }

  ngOnInit(): void {

    const claim = this.view.currentClaim();
    if (leaveLink(claim?.recordSource)) {
      this.level.setValue('employee', { emitEvent: false });
      this.level.disable({ emitEvent: false });
    }

    // Get data from resolver - no API call needed
    const rawAbsences = this.activatedRoute.snapshot.data['absences'] || [];
    this.absences = filterAbsencesForCalendar(rawAbsences);

    // Initialize dropdown options and apply initial filters
    // Claim selector functionality - commented out as claim dropdown is not currently visible
    // this.updateClaimsDropdown();
    // this.initializeClaimSelection();
    this.updateFiltersAndView();

    this.view.hideLoading();
  }

  onTabChange(tabValue: string): void {
    // Map tab labels to indices for lazy loading
    const tabMap: Record<string, number> = {
      'Yearly view': 0,
      'Monthly view': 1,
      'Weekly view': 2,
    };
    this.activeTabIndex = tabMap[tabValue] ?? 0;
  }

  // Combined filter, index, and date menu update in single pass for optimal performance
  private updateFiltersAndView(): void {
    if (!this.absences?.length) {
      this.filteredAbsences = [];
      this.approvedAbsences = [];
      this.absencesByDate = new Map();
      this.updateDateMenuForEmpty();
      return;
    }

    const newMap = new Map<string, LeaveCalendar[]>();
    const filteredList: LeaveCalendar[] = [];
    let minTime = Infinity;
    let maxTime = -Infinity;

    // Single pass: filter, index, and track min/max dates
    for (const absence of this.absences) {
      // Apply filter logic
      // Employee level: show all records (already filtered by status in ngOnInit)
      // Claim level: show absences for the current claim number (default selected claim)
      const currentClaim = this.view.currentClaim();
      const shouldInclude =
        this.level.value === 'claim' && currentClaim?.fileNumber
          ? absence.absenceNumber === currentClaim.fileNumber
          : true; // Employee level shows all

      if (shouldInclude) {
        filteredList.push(absence);

        // Index by date
        const dateKey = getDateKey(new Date(absence.date));
        if (!newMap.has(dateKey)) {
          newMap.set(dateKey, []);
        }
        newMap.get(dateKey)!.push(absence);

        // Track min/max for date menu
        const time = new Date(absence.date).getTime();
        if (time < minTime) minTime = time;
        if (time > maxTime) maxTime = time;
      }
    }

    this.filteredAbsences = filteredList;
    this.approvedAbsences = filteredList.filter(
      (absence) => absence.status?.toLowerCase() === 'approved',
    );
    this.absencesByDate = newMap;
    this.updateDateMenuFromMinMax(minTime, maxTime);
  }

  private updateDateMenuForEmpty(): void {
    const todayDate = new Date();
    const firstDay = new Day(todayDate, 'First absence');
    const lastDay = new Day(todayDate, 'Last absence');

    (firstDay as any).disabled = true;
    (lastDay as any).disabled = true;

    this.dateMenu = [new Day(todayDate, 'Today'), firstDay, lastDay];
  }

  private updateDateMenuFromMinMax(minTime: number, maxTime: number): void {
    if (minTime === Infinity || maxTime === -Infinity) {
      this.updateDateMenuForEmpty();
      return;
    }

    const minDate = new Date(minTime);
    const maxDate = new Date(maxTime);

    // Create new array with new Day instances to ensure reference changes
    const newDateMenu = [
      new Day(new Date(), 'Today'),
      new Day(minDate, 'First absence'),
      new Day(maxDate, 'Last absence'),
    ];

    // Force reference change by assigning new array
    this.dateMenu = [...newDateMenu];
  }

  // Claim selector functionality - commented out as claim dropdown is not currently visible
  // This could be re-enabled down the line if needed for filtering by specific claims

  // 1. Update claims dropdown ONLY when raw absences change
  /* private updateClaimsDropdown(): void {
    if (!this.absences?.length) {
      this.claims = [{ description: 'Claim Number', value: '' }];
      return;
    }
    
    const uniqueClaimsMap = new Map<string, { description: string; value: string }>();
    for (const absence of this.absences) {
      if (!uniqueClaimsMap.has(absence.absenceNumber)) {
        uniqueClaimsMap.set(absence.absenceNumber, {
          description: absence.absenceNumber,
          value: absence.absenceNumber
        });
      }
    }
    this.claims = [
      { description: 'Claim Number', value: '' },
      ...uniqueClaimsMap.values()
    ];
  } */

  // 2. Initialize claim selection based on current claim (only on first load)
  /* private initializeClaimSelection(): void {
    if (this.isClaimInitialized) {
      return;
    }

    const currentClaim = this.view.currentClaim();
    if (currentClaim?.fileNumber && this.level.value === 'claim') {
      // Check if fileNumber exists in the claims dropdown list
      const matchingClaim = this.claims.find(c => c.value === currentClaim.fileNumber);
      if (matchingClaim) {
        this.claim.setValue(currentClaim.fileNumber, { emitEvent: false });
      }
    }
    this.isClaimInitialized = true;
  } */

  async onExport(format: ExportFormat): Promise<void> {
    if (!this.absences || this.absences.length === 0) {
      return;
    }

    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `LeaveCalendarSummary_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = LeaveExportMapper.toCsvExport(
        this.absences,
        this.exportColumns,
        'LeaveCalendarSummary',
      );
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = LeaveExportMapper.toExcelExport(
        this.absences,
        this.exportColumns,
        'LeaveCalendarSummary',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }
}
